<script setup lang="ts">
import { ref, computed } from 'vue';
import ValidationInput, { type ValidationInputCustomisation } from './ValidationInput.vue';
import PlsButton1 from './PlsButton1.vue';
import { MAX_DESCRIPTION_CHAR_NUM } from '@/common/config';

export interface ButtonCustomisation {
  text?: string;
  borderColor?: string;
  backgroundColor?: string;
  textColor?: string;
}

export interface TextSubmitFormCustomisation {
  input?: ValidationInputCustomisation;
  cancelButton?: ButtonCustomisation;
  submitButton?: ButtonCustomisation;
  submitButtonDisabled?: ButtonCustomisation;
}

export interface TextSubmitFormProps {
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  customisation?: TextSubmitFormCustomisation;
  disabled?: boolean;
  initialValue?: string;
}

const props = withDefaults(defineProps<TextSubmitFormProps>(), {
  placeholder: 'Enter your message...',
  maxLength: MAX_DESCRIPTION_CHAR_NUM,
  minLength: 3,
  disabled: false,
  initialValue: '',
});

const emit = defineEmits<{
  (e: 'submit', value: string): void;
  (e: 'cancel'): void;
}>();

const inputValue = ref(props.initialValue);

const isSubmitDisabled = computed(() => {
  if (props.disabled) return true;
  const trimmedValue = inputValue.value.trim();
  return trimmedValue.length < props.minLength;
});

const handleInput = (value: string) => {
  inputValue.value = value;
};

const handleSubmit = () => {
  if (isSubmitDisabled.value) return;
  emit('submit', inputValue.value.trim());
};

const handleCancel = () => {
  inputValue.value = '';
  emit('cancel');
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && event.ctrlKey) {
    handleSubmit();
  }
};
</script>

<template>
  <div class="text-submit-form" @keydown="handleKeyDown">
    <ValidationInput
      tag="textarea"
      :value="inputValue"
      :placeholder="`${placeholder}`"
      :max-length="maxLength"
      :disabled="disabled"
      :show-error-text-block="false"
      :customisation="customisation?.input"
      data-testid="text-submit-input"
      class="text-submit-form__input"
      @input="handleInput"
    />

    <div class="text-submit-form__actions">
      <PlsButton1
        :text="customisation?.cancelButton?.text"
        :border-color="customisation?.cancelButton?.borderColor"
        :background-color="customisation?.cancelButton?.backgroundColor"
        :text-color="customisation?.cancelButton?.textColor"
        :disabled="disabled"
        data-testid="text-submit-cancel"
        class="text-submit-form__button text-submit-form__button--cancel"
        @click="handleCancel"
      />

      <PlsButton1
        :text="customisation?.submitButton?.text"
        :border-color="
          isSubmitDisabled ? customisation?.submitButtonDisabled?.borderColor : customisation?.submitButton?.borderColor
        "
        :background-color="
          isSubmitDisabled
            ? customisation?.submitButtonDisabled?.backgroundColor
            : customisation?.submitButton?.backgroundColor
        "
        :text-color="
          isSubmitDisabled ? customisation?.submitButtonDisabled?.textColor : customisation?.submitButton?.textColor
        "
        :disabled="isSubmitDisabled"
        data-testid="text-submit-submit"
        class="text-submit-form__button text-submit-form__button--submit"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.text-submit-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 12px;
}

.text-submit-form__input {
  width: 100%;
}

.text-submit-form__actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.text-submit-form__button {
  flex: 1;
  max-width: 120px;
}
</style>
